/**
 * T1030cr · Lead Pipeline & Automated Outreach
 *
 * Click-to-advance Kanban with five fixed stages.  The operator can
 * promote any card to the next stage, and the right-rail "Sequence"
 * panel shows the three-touch outreach (SMS → Email → Voice) that will
 * fire automatically.  All actions are sandboxed via toast.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import ActiveCardShell from "./ActiveCardShell";
import { useDemoBrand, brandText } from "@/lib/demoBrand";
import { Workflow, MessageSquare, Mail, Phone, ArrowRight, Send } from "lucide-react";

const STAGES = [
  { id: "scraped",     label: "Scraped" },
  { id: "contacted",   label: "Contacted" },
  { id: "qualified",   label: "Qualified" },
  { id: "statement",   label: "Statement In" },
  { id: "won",         label: "Won" },
];

const nextStage = (s) => {
  const i = STAGES.findIndex((x) => x.id === s);
  return i >= 0 && i < STAGES.length - 1 ? STAGES[i + 1].id : s;
};

const DEFAULT_SEQ = [
  { id: "sms",   Icon: MessageSquare, label: "SMS · Touch 1",   body: "Hi {owner}, this is Mia from VEROOGLE — we help {biz} cut processing fees by ~22% without changing your terminal. Open to a 2-min peek?" },
  { id: "email", Icon: Mail,          label: "Email · Touch 2", body: "Subject: 22% effective rate · {biz}\n\nHi {owner}, following up with a quick side-by-side on your current statement vs the VEROOGLE buy-rate. 90 seconds, no commitment." },
  { id: "voice", Icon: Phone,         label: "Voice · Touch 3", body: "{owner}, this is Mia at VEROOGLE. Left a note about a side-by-side rate analysis for {biz} — you'll see the delta on the first page of the breakdown. Grab me when you can." },
];

export default function LeadPipelineCard({ pipeline, setPipeline }) {
  const { name } = useDemoBrand();
  const [activeSeq, setActiveSeq] = useState("sms");

  const advance = (leadId) => {
    setPipeline((prev) => prev.map((l) => l.id === leadId ? { ...l, stage: nextStage(l.stage) } : l));
    toast.success("Lead advanced — sequence auto-queued");
  };

  const grouped = STAGES.reduce((acc, s) => {
    acc[s.id] = pipeline.filter((l) => l.stage === s.id);
    return acc;
  }, {});

  const seq = DEFAULT_SEQ.find((s) => s.id === activeSeq);
  const ActiveIcon = seq?.Icon || MessageSquare;

  return (
    <ActiveCardShell id="T1030cr" kicker="PIPELINE" title="Lead Pipeline & Automated Outreach" Icon={Workflow}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5">
        {/* Kanban */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="veroogle-t1030-kanban">
          {STAGES.map((s) => (
            <div key={s.id} data-testid={`veroogle-t1030-col-${s.id}`} className="rounded-xl border border-white/8 bg-white/[0.02] flex flex-col overflow-hidden">
              <div className="px-3 py-2 border-b border-white/8 bg-white/[0.03] flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-300">{s.label}</span>
                <span className="font-mono text-[10px] text-brand-cyan">{grouped[s.id].length}</span>
              </div>
              <div className="flex flex-col gap-2 p-2 min-h-[160px]">
                {grouped[s.id].map((lead) => (
                  <div key={lead.id} className="rounded-lg border border-white/10 bg-navy-900/60 p-2.5 group">
                    <div className="text-white text-xs font-medium truncate">{lead.name}</div>
                    <div className="text-slate-500 font-mono text-[10px] mt-0.5">${lead.volume.toLocaleString()}/mo · {lead.score ?? "—"}</div>
                    {s.id !== "won" && (
                      <button
                        onClick={() => advance(lead.id)}
                        data-testid={`veroogle-t1030-advance-${lead.id}`}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1 text-brand-emerald hover:text-emerald-300 font-mono text-[9px] tracking-[0.14em] uppercase transition"
                      >
                        Advance <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                {grouped[s.id].length === 0 && (
                  <div className="text-slate-600 text-[10px] font-mono text-center py-3">—</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Sequence editor */}
        <aside className="rounded-xl border border-white/8 bg-white/[0.02] p-3 flex flex-col gap-3">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Outreach sequence</div>
          <div className="flex gap-1" data-testid="veroogle-t1030-seq-tabs">
            {DEFAULT_SEQ.map((s) => {
              const Icon = s.Icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSeq(s.id)}
                  data-testid={`veroogle-t1030-seq-${s.id}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1 py-1.5 rounded-md font-mono text-[9px] tracking-[0.14em] uppercase transition ${
                    activeSeq === s.id ? "bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/40" : "text-slate-400 hover:text-slate-200 border border-white/5"
                  }`}
                >
                  <Icon className="w-3 h-3" /> {s.id.toUpperCase()}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 text-slate-300 font-mono text-[11px] tracking-[0.14em] uppercase">
            <ActiveIcon className="w-3.5 h-3.5 text-brand-cyan" /> {seq?.label}
          </div>
          <textarea
            data-testid="veroogle-t1030-seq-body"
            value={brandText(seq?.body || "", name)}
            onChange={() => { /* sandboxed: edits allowed but ephemeral */ }}
            rows={6}
            className="w-full rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-slate-200 font-mono resize-none"
          />
          <button
            data-testid="veroogle-t1030-send-touch"
            onClick={() => toast.success(`${seq.label} fired to all ${pipeline.length} leads · sandboxed`)}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-xs font-semibold tracking-[0.14em] uppercase py-2 transition"
          >
            <Send className="w-3.5 h-3.5" /> Send touch now
          </button>
        </aside>
      </div>
    </ActiveCardShell>
  );
}
